import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostListener,
	NgZone,
	OnDestroy,
	ViewChild,
} from '@angular/core';
import {
	MAX_ANGLE,
	MAX_POWER,
	MAX_WIND,
	MIN_ANGLE,
	MIN_POWER,
	TANK_HEIGHT,
	TANK_WIDTH,
	TICK_MS,
	WEAPON_ORDER,
	WEAPONS,
	WORLD_HEIGHT,
	WORLD_WIDTH,
	cloneInventory,
} from './scorched-earth.constants';
import {
	beginGame,
	buyWeapon,
	enterShopPhase,
	finishShop,
	fireWeapon,
	getCurrentPlayer,
	passTurn,
	restartGame,
	setPlayerCount,
	setPlayerName,
	setCombatantKind,
	setCpuDifficulty,
	setBestOf,
	tickNapalmEffects,
	tickProjectiles,
	tickTurnTimer,
	totalAmmo,
	updateAim,
} from './scorched-earth.engine';
import { applyCpuTurn } from './scorched-earth.cpu';
import { computeEnvironment, colorWithAlpha, EnvironmentSnapshot } from './scorched-earth.environment';
import { buildTrajectoryPreview } from './scorched-earth.physics';
import {
	applyScorchMark,
	buildStrataPalette,
	createScorchField,
	craterRimStrength,
	localSlopeAt,
	mixTerrainColors,
	terrainColorAt,
	TerrainStratum,
} from './scorched-earth.terrain';
import { createInitialState } from './scorched-earth.engine';
import { CombatantKind, CpuDifficulty, Inventory, ScorchedEarthState, Tank, WeaponId } from './scorched-earth.types';
import { WEAPON_VISUALS } from './scorched-earth.visuals';
import {
	BestScoreEntry,
	BestScoresStore,
	buildMatchStandings,
	computePlayerMatchScore,
	emptyBestScoresStore,
	loadBestScoresFromStorage,
	MatchStanding,
	mergeBestScores,
	personalBestForName,
	saveBestScoresToStorage,
	SCORE_PER_CREDIT,
	SCORE_PER_HEALTH,
	SCORE_PER_ROUND_WIN,
} from './scorched-earth.score';

const PERSIST_KEY = 'scorched-earth-progress-v1';

interface PersistedProgress {
	version: 1;
	seed: number;
	bestOf: number;
	round: number;
	selectedWeapon: WeaponId;
	players: Array<{
		id: number;
		name: string;
		kind: CombatantKind;
		difficulty: CpuDifficulty | null;
		roundWins: number;
		credits: number;
		inventory: Inventory;
		color: string;
	}>;
}

interface WindParticle {
	x: number;
	y: number;
	speed: number;
	size: number;
	alpha: number;
}

interface DebrisParticle {
	x: number;
	y: number;
	vx: number;
	vy: number;
	life: number;
	maxLife: number;
	size: number;
	hue: number;
}

@Component({
	selector: 'app-scorched-earth',
	templateUrl: './scorched-earth.component.html',
	styleUrls: ['./scorched-earth.component.scss'],
})
export class ScorchedEarthComponent implements AfterViewInit, OnDestroy {
	@ViewChild('gameRoot') gameRoot!: ElementRef<HTMLDivElement>;
	@ViewChild('gameCanvas') gameCanvas!: ElementRef<HTMLCanvasElement>;

	readonly title = 'Scorched Earth';
	readonly weapons = WEAPON_ORDER.map((id) => WEAPONS[id]);
	readonly minAngle = MIN_ANGLE;
	readonly maxAngle = MAX_ANGLE;
	readonly minPower = MIN_POWER;
	readonly maxPower = MAX_POWER;

	state: ScorchedEarthState = createInitialState(1337, 2);
	playerCount = 2;
	reduceMotion = false;
	isFullscreen = false;
	bestScores: BestScoresStore = emptyBestScoresStore();
	readonly scoreRuleLabel = `Score = wins×${SCORE_PER_ROUND_WIN} + credits×${SCORE_PER_CREDIT} + HP×${SCORE_PER_HEALTH}`;

	private ctx: CanvasRenderingContext2D | null = null;
	private projectileTimer: ReturnType<typeof setInterval> | null = null;
	private napalmTimer: ReturnType<typeof setInterval> | null = null;
	private turnTimer: ReturnType<typeof setInterval> | null = null;
	private projectileLoopActive = false;
	private turnLoopActive = false;
	private napalmLoopActive = false;
	private projectileSimClockAt = 0;
	private turnSimClockAt = 0;
	private napalmSimClockAt = 0;
	private cpuTimer: ReturnType<typeof setTimeout> | null = null;
	private cpuScheduledForIndex = -1;
	private focusTimer: ReturnType<typeof setTimeout> | null = null;
	private resizeObserver: ResizeObserver | null = null;
	private dpr = 1;
	private canvasWidth = WORLD_WIDTH;
	private canvasHeight = WORLD_HEIGHT;
	private scorchField = createScorchField();
	private seenExplosionIds = new Set<number>();
	private strata: TerrainStratum[] = buildStrataPalette(1337);
	private debrisFx: DebrisParticle[] = [];
	private windParticles: WindParticle[] = [];
	private tankRecoil = new Map<number, number>();
	private cameraX = 0;
	private cameraY = 0;
	private animTime = 0;
	private ambientFrame: number | null = null;
	private lastTerrainSeed = -1;
	private lastRound = -1;
	private removeFullscreenListener: (() => void) | null = null;

	constructor(
		private readonly cdr: ChangeDetectorRef,
		private readonly zone: NgZone,
	) {
		this.bestScores = loadBestScoresFromStorage(typeof localStorage !== 'undefined' ? localStorage : null);
		this.hydratePersistedProgress();
	}

	ngAfterViewInit(): void {
		this.reduceMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		this.initCanvas();
		this.initWindParticles();
		this.focusTimer = setTimeout(() => this.focusGame(), 120);
		this.syncTimers();
		this.bindFullscreenListener();
		if (!this.reduceMotion) {
			this.startAmbientLoop();
		}
	}

	ngOnDestroy(): void {
		this.stopAmbientLoop();
		this.stopProjectileLoop();
		this.stopNapalmTimer();
		this.stopTurnTimer();
		this.stopCpuTimer();
		if (this.focusTimer) {
			clearTimeout(this.focusTimer);
			this.focusTimer = null;
		}
		this.resizeObserver?.disconnect();
		this.resizeObserver = null;
		this.unbindFullscreenListener();
		this.ctx = null;
	}

	get currentPlayer() {
		return getCurrentPlayer(this.state);
	}

	get inventoryOwner(): Tank | null {
		return this.currentPlayer ?? this.state.players[0] ?? null;
	}

	get isHumanAiming(): boolean {
		return this.state.phase === 'aiming' && this.currentPlayer?.kind === 'human';
	}

	get isCpuAiming(): boolean {
		return this.state.phase === 'aiming' && this.currentPlayer?.kind === 'cpu';
	}

	get showInventoryRail(): boolean {
		return this.state.phase === 'aiming' || this.state.phase === 'projectile';
	}

	get environment(): EnvironmentSnapshot {
		return computeEnvironment(this.state.seed, this.state.round, this.animTime);
	}

	get windLabel(): string {
		const w = this.state.wind;
		if (w === 0) {
			return 'Calm';
		}
		return w > 0 ? `${w} →` : `${Math.abs(w)} ←`;
	}

	get weatherLabel(): string {
		return this.environment.weatherLabel;
	}

	get timeLabel(): string {
		return this.environment.timeLabel;
	}

	get canFire(): boolean {
		const p = this.currentPlayer;
		return this.state.phase === 'aiming' && !!p?.alive && p.inventory[this.state.selectedWeapon] > 0;
	}

	get canPass(): boolean {
		const p = this.currentPlayer;
		return this.state.phase === 'aiming' && !!p?.alive && totalAmmo(p) === 0;
	}

	get showOverlay(): boolean {
		return ['setup', 'shop', 'roundEnd', 'gameOver'].includes(this.state.phase);
	}

	get matchStandings(): MatchStanding[] {
		return buildMatchStandings(this.state.players);
	}

	get localBestEntries(): BestScoreEntry[] {
		return this.bestScores.entries;
	}

	playerScore(player: Tank): number {
		return computePlayerMatchScore(player);
	}

	personalBest(name: string): BestScoreEntry | null {
		return personalBestForName(this.bestScores, name);
	}

	focusGame(): void {
		this.gameRoot?.nativeElement?.focus();
	}

	trackWeapon(_i: number, w: { id: WeaponId }): string {
		return w.id;
	}

	trackPlayer(_i: number, p: { id: number }): number {
		return p.id;
	}

	onPlayerCountChange(count: number): void {
		this.playerCount = count;
		this.applyState(setPlayerCount(this.state, count));
	}

	onPlayerNameChange(playerId: number, event: Event): void {
		const value = (event.target as HTMLInputElement).value;
		this.applyState(setPlayerName(this.state, playerId, value));
	}

	onCombatantKindChange(playerId: number, kind: CombatantKind): void {
		this.applyState(setCombatantKind(this.state, playerId, kind));
	}

	onCpuDifficultyChange(playerId: number, difficulty: CpuDifficulty): void {
		this.applyState(setCpuDifficulty(this.state, playerId, difficulty));
	}

	onBestOfChange(bestOf: number): void {
		this.applyState(setBestOf(this.state, bestOf));
	}

	onAngleChange(value: number): void {
		this.applyState(updateAim(this.state, { angle: value }));
	}

	onPowerChange(value: number): void {
		this.applyState(updateAim(this.state, { power: value }));
	}

	selectWeapon(id: WeaponId): void {
		this.applyState(updateAim(this.state, { weaponId: id }));
	}

	startBattle(): void {
		this.applyState(beginGame(this.state));
		this.focusGame();
	}

	fire(): void {
		if (!this.canFire) {
			return;
		}
		this.applyState(fireWeapon(this.state));
	}

	pass(): void {
		if (!this.canPass) {
			return;
		}
		this.applyState(passTurn(this.state));
	}

	openShop(): void {
		this.applyState(enterShopPhase(this.state));
	}

	buy(playerId: number, weaponId: WeaponId): void {
		this.applyState(buyWeapon(this.state, playerId, weaponId));
	}

	leaveShop(): void {
		this.applyState(finishShop(this.state));
		this.focusGame();
	}

	restart(): void {
		this.clearPersistedProgress();
		this.applyState(restartGame(this.state));
		this.playerCount = this.state.players.length;
		this.focusGame();
	}

	async toggleFullscreen(): Promise<void> {
		const root = this.gameRoot?.nativeElement;
		if (!root || typeof document === 'undefined') {
			return;
		}
		try {
			if (document.fullscreenElement) {
				await document.exitFullscreen();
			} else {
				const req =
					root.requestFullscreen?.bind(root) ||
					(root as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> }).webkitRequestFullscreen?.bind(
						root,
					);
				if (req) {
					await req();
				}
			}
		} catch {
			// Headless / permission-denied: still sync flag from document state.
		}
		this.syncFullscreenFlag();
		this.resizeCanvas();
		this.focusGame();
	}

	adjustAngle(delta: number): void {
		const p = this.currentPlayer;
		if (!p || this.state.phase !== 'aiming') {
			return;
		}
		this.applyState(updateAim(this.state, { angle: p.angle + delta }));
	}

	adjustPower(delta: number): void {
		const p = this.currentPlayer;
		if (!p || this.state.phase !== 'aiming') {
			return;
		}
		this.applyState(updateAim(this.state, { power: p.power + delta }));
	}

	onTouchControl(event: PointerEvent, action: () => void): void {
		if (event.pointerType === 'mouse') {
			return;
		}
		event.preventDefault();
		action();
	}

	onTouchAngleDown(event: PointerEvent): void {
		this.onTouchControl(event, () => this.adjustAngle(-3));
	}

	onTouchAngleUp(event: PointerEvent): void {
		this.onTouchControl(event, () => this.adjustAngle(3));
	}

	onTouchPowerDown(event: PointerEvent): void {
		this.onTouchControl(event, () => this.adjustPower(-3));
	}

	onTouchPowerUp(event: PointerEvent): void {
		this.onTouchControl(event, () => this.adjustPower(3));
	}

	onTouchFire(event: PointerEvent): void {
		this.onTouchControl(event, () => this.fire());
	}

	cycleWeapon(dir: 1 | -1): void {
		const p = this.currentPlayer;
		if (!p) {
			return;
		}
		const idx = WEAPON_ORDER.indexOf(this.state.selectedWeapon);
		for (let step = 1; step <= WEAPON_ORDER.length; step++) {
			const next = WEAPON_ORDER[(idx + dir * step + WEAPON_ORDER.length) % WEAPON_ORDER.length];
			if (p.inventory[next] > 0) {
				this.selectWeapon(next);
				return;
			}
		}
	}

	@HostListener('window:resize')
	onResize(): void {
		this.resizeCanvas();
	}

	onKeyDown(event: KeyboardEvent): void {
		if (event.key === 'f' || event.key === 'F') {
			event.preventDefault();
			void this.toggleFullscreen();
			return;
		}
		if (this.state.phase === 'setup' && event.key === 'Enter') {
			event.preventDefault();
			this.startBattle();
			return;
		}
		if (this.state.phase === 'roundEnd' && event.key === 'Enter') {
			event.preventDefault();
			this.openShop();
			return;
		}
		if (this.state.phase === 'shop' && event.key === 'Enter') {
			event.preventDefault();
			this.leaveShop();
			return;
		}
		if (this.state.phase === 'gameOver' && event.key === 'Enter') {
			event.preventDefault();
			this.restart();
			return;
		}
		if (this.state.phase !== 'aiming') {
			return;
		}
		switch (event.key) {
		case 'ArrowLeft':
		case 'a':
		case 'A':
			event.preventDefault();
			this.adjustAngle(-2);
			break;
		case 'ArrowRight':
		case 'd':
		case 'D':
			event.preventDefault();
			this.adjustAngle(2);
			break;
		case 'ArrowUp':
		case 'w':
		case 'W':
			event.preventDefault();
			this.adjustPower(2);
			break;
		case 'ArrowDown':
		case 's':
		case 'S':
			event.preventDefault();
			this.adjustPower(-2);
			break;
		case ' ':
			event.preventDefault();
			if (this.canFire) {
				this.fire();
			} else if (this.canPass) {
				this.pass();
			}
			break;
		case 'p':
		case 'P':
			event.preventDefault();
			this.pass();
			break;
		case 'Tab':
			event.preventDefault();
			this.cycleWeapon(event.shiftKey ? -1 : 1);
			break;
		default: {
			const num = parseInt(event.key, 10);
			if (num >= 1 && num <= 6) {
				event.preventDefault();
				this.selectWeapon(WEAPON_ORDER[num - 1]);
			}
		}
		}
	}

	private initCanvas(): void {
		const canvas = this.gameCanvas.nativeElement;
		canvas.style.touchAction = 'none';
		this.ctx = canvas.getContext('2d');
		this.resizeObserver = new ResizeObserver(() => this.resizeCanvas());
		this.resizeObserver.observe(this.gameRoot.nativeElement);
		this.resizeCanvas();
		this.syncTerrainFx();
		this.render();
	}

	private resizeCanvas(): void {
		const host = this.gameRoot.nativeElement;
		const rect = host.getBoundingClientRect();
		this.dpr = Math.min(window.devicePixelRatio || 1, 2);
		const width = Math.max(280, Math.floor(rect.width));
		const height = Math.floor(width * (WORLD_HEIGHT / WORLD_WIDTH));
		this.canvasWidth = width;
		this.canvasHeight = height;
		const canvas = this.gameCanvas.nativeElement;
		canvas.width = Math.floor(width * this.dpr);
		canvas.height = Math.floor(height * this.dpr);
		canvas.style.width = `${width}px`;
		canvas.style.height = `${height}px`;
		this.ctx?.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
		this.render();
	}

	private applyState(next: ScorchedEarthState): void {
		const apply = () => {
			const prev = this.state;
			if (prev.currentPlayerIndex !== next.currentPlayerIndex && next.phase === 'projectile') {
				const shooter = prev.players[prev.currentPlayerIndex];
				if (shooter) {
					this.tankRecoil.set(shooter.id, 1);
				}
			}
			this.state = next;
			if (prev.phase !== 'gameOver' && next.phase === 'gameOver') {
				this.recordMatchBests();
			}
			this.persistProgress();
			this.syncTerrainFx();
			this.syncExplosionFx();
			this.render();
			this.syncTimers();
			this.cdr.detectChanges();
		};
		if (NgZone.isInAngularZone()) {
			apply();
		} else {
			this.zone.run(apply);
		}
	}

	private syncTimers(): void {
		if (this.state.phase === 'projectile') {
			this.startProjectileLoop();
		} else {
			this.stopProjectileLoop();
		}
		if (this.state.phase === 'aiming' && this.state.napalmZones.length > 0) {
			this.startNapalmTimer();
		} else {
			this.stopNapalmTimer();
		}
		if (this.state.phase === 'aiming' && this.state.turnTimerRemaining > 0) {
			this.startTurnTimer();
		} else {
			this.stopTurnTimer();
		}
		this.syncCpuTurn();
	}

	private syncCpuTurn(): void {
		const player = this.currentPlayer;
		if (this.state.phase !== 'aiming' || !player || player.kind !== 'cpu') {
			this.stopCpuTimer();
			this.cpuScheduledForIndex = -1;
			return;
		}
		if (this.cpuScheduledForIndex === this.state.currentPlayerIndex && this.cpuTimer !== null) {
			return;
		}
		this.stopCpuTimer();
		this.cpuScheduledForIndex = this.state.currentPlayerIndex;
		this.cpuTimer = setTimeout(() => {
			this.cpuTimer = null;
			this.cpuScheduledForIndex = -1;
			this.executeCpuTurn();
		}, 350);
	}

	private executeCpuTurn(): void {
		if (this.state.phase !== 'aiming') {
			return;
		}
		const player = this.currentPlayer;
		if (!player || player.kind !== 'cpu') {
			return;
		}
		this.applyState(applyCpuTurn(this.state));
	}

	private startTurnTimer(): void {
		if (this.turnLoopActive) {
			return;
		}
		this.turnLoopActive = true;
		this.turnSimClockAt = performance.now();
		if (this.reduceMotion) {
			this.turnTimer = setInterval(() => this.runTurnTicks(), TICK_MS);
		}
	}

	private runTurnTicks(): void {
		if (!this.turnLoopActive || this.state.phase !== 'aiming') {
			this.stopTurnTimer();
			return;
		}
		const now = performance.now();
		let steps = Math.max(1, Math.floor((now - this.turnSimClockAt) / TICK_MS));
		steps = Math.min(steps, 120);
		this.turnSimClockAt += steps * TICK_MS;
		for (let i = 0; i < steps; i++) {
			if (this.state.phase !== 'aiming') {
				break;
			}
			const next = tickTurnTimer(this.state);
			if (next === this.state) {
				continue;
			}
			if (next.phase === 'aiming' && next.turnTimerRemaining !== this.state.turnTimerRemaining) {
				this.state = next;
				continue;
			}
			this.applyState(next);
			return;
		}
		this.render();
	}

	private stopTurnTimer(): void {
		this.turnLoopActive = false;
		if (this.turnTimer !== null) {
			clearInterval(this.turnTimer);
			this.turnTimer = null;
		}
	}

	private stopCpuTimer(): void {
		if (this.cpuTimer !== null) {
			clearTimeout(this.cpuTimer);
			this.cpuTimer = null;
		}
	}

	private startProjectileLoop(): void {
		if (this.projectileLoopActive) {
			return;
		}
		this.projectileLoopActive = true;
		this.projectileSimClockAt = performance.now();
		if (this.reduceMotion) {
			this.projectileTimer = setInterval(() => this.runProjectileTicks(), TICK_MS);
		}
	}

	private runProjectileTicks(): void {
		if (!this.projectileLoopActive || this.state.phase !== 'projectile') {
			this.stopProjectileLoop();
			return;
		}
		const now = performance.now();
		let steps = Math.max(1, Math.floor((now - this.projectileSimClockAt) / TICK_MS));
		steps = Math.min(steps, 120);
		this.projectileSimClockAt += steps * TICK_MS;
		for (let i = 0; i < steps; i++) {
			if (this.state.phase !== 'projectile') {
				break;
			}
			const next = tickProjectiles(this.state);
			if (next.phase !== 'projectile') {
				this.applyState(next);
				return;
			}
			this.state = next;
		}
		this.syncExplosionFx();
		this.render();
	}

	private stopProjectileLoop(): void {
		this.projectileLoopActive = false;
		if (this.projectileTimer !== null) {
			clearInterval(this.projectileTimer);
			this.projectileTimer = null;
		}
	}

	private startNapalmTimer(): void {
		if (this.napalmLoopActive) {
			return;
		}
		this.napalmLoopActive = true;
		this.napalmSimClockAt = performance.now();
		if (this.reduceMotion) {
			this.napalmTimer = setInterval(() => this.runNapalmTicks(), TICK_MS);
		}
	}

	private runNapalmTicks(): void {
		if (!this.napalmLoopActive || this.state.phase !== 'aiming' || !this.state.napalmZones.length) {
			this.stopNapalmTimer();
			return;
		}
		const now = performance.now();
		let steps = Math.max(1, Math.floor((now - this.napalmSimClockAt) / TICK_MS));
		steps = Math.min(steps, 120);
		this.napalmSimClockAt += steps * TICK_MS;
		for (let i = 0; i < steps; i++) {
			if (this.state.phase !== 'aiming' || !this.state.napalmZones.length) {
				break;
			}
			this.state = tickNapalmEffects(this.state);
		}
		this.render();
		this.syncTimers();
	}

	private stopNapalmTimer(): void {
		this.napalmLoopActive = false;
		if (this.napalmTimer !== null) {
			clearInterval(this.napalmTimer);
			this.napalmTimer = null;
		}
	}

	private startAmbientLoop(): void {
		if (this.ambientFrame !== null || this.reduceMotion) {
			return;
		}
		this.zone.runOutsideAngular(() => {
			const loop = (ts: number) => {
				this.animTime = ts * 0.001;
				this.tickRecoil();
				this.tickDebris();
				this.updateWindParticles();
				this.updateCamera();
				if (this.projectileLoopActive) {
					this.runProjectileTicks();
				}
				if (this.turnLoopActive) {
					this.runTurnTicks();
				}
				if (this.napalmLoopActive) {
					this.runNapalmTicks();
				}
				this.render();
				this.ambientFrame = requestAnimationFrame(loop);
			};
			this.ambientFrame = requestAnimationFrame(loop);
		});
	}

	private stopAmbientLoop(): void {
		if (this.ambientFrame !== null) {
			cancelAnimationFrame(this.ambientFrame);
			this.ambientFrame = null;
		}
	}

	private syncTerrainFx(): void {
		if (this.state.seed !== this.lastTerrainSeed || this.state.round !== this.lastRound) {
			this.scorchField = createScorchField();
			this.seenExplosionIds.clear();
			this.debrisFx = [];
			this.strata = buildStrataPalette(this.state.seed + this.state.round * 31);
			this.lastTerrainSeed = this.state.seed;
			this.lastRound = this.state.round;
		}
	}

	private syncExplosionFx(): void {
		for (const ex of this.state.explosions) {
			if (this.seenExplosionIds.has(ex.id)) {
				continue;
			}
			this.seenExplosionIds.add(ex.id);
			const visual = WEAPON_VISUALS[ex.weaponId];
			const scorchIntensity =
				ex.weaponId === 'nuke' || ex.weaponId === 'babyNuke' ? 1 : ex.weaponId === 'napalm' ? 0.55 : 0.75;
			applyScorchMark(this.scorchField, ex.x, ex.radius, scorchIntensity);
			if (!this.reduceMotion) {
				this.spawnDebris(ex.x, ex.y, ex.radius, ex.weaponId, visual.debrisCount, visual.debrisHue);
			}
		}
	}

	private spawnDebris(x: number, y: number, radius: number, weaponId: WeaponId, count: number, hue: number): void {
		for (let i = 0; i < count; i++) {
			const angle = (i / count) * Math.PI * 2 + this.state.seed * 0.01;
			const speed = (0.4 + (i % 5) * 0.15) * (radius / 20);
			this.debrisFx.push({
				x,
				y,
				vx: Math.cos(angle) * speed,
				vy: Math.sin(angle) * speed - 0.6,
				life: 0,
				maxLife: 28 + (i % 6),
				size: 1.5 + (i % 3),
				hue,
			});
		}
	}

	private tickDebris(): void {
		if (!this.debrisFx.length) {
			return;
		}
		this.debrisFx = this.debrisFx
			.map((d) => ({
				...d,
				x: d.x + d.vx,
				y: d.y + d.vy,
				vy: d.vy + 0.04,
				life: d.life + 1,
			}))
			.filter((d) => d.life < d.maxLife);
	}

	private tickRecoil(): void {
		for (const [id, amount] of this.tankRecoil.entries()) {
			const next = Math.max(0, amount - 0.12);
			if (next <= 0.01) {
				this.tankRecoil.delete(id);
			} else {
				this.tankRecoil.set(id, next);
			}
		}
	}

	private initWindParticles(): void {
		this.windParticles = Array.from({ length: 24 }, (_, i) => ({
			x: (i * 37) % WORLD_WIDTH,
			y: 18 + (i * 13) % 80,
			speed: 0.6 + (i % 5) * 0.2,
			size: 1 + (i % 3),
			alpha: 0.15 + (i % 4) * 0.08,
		}));
	}

	private updateWindParticles(): void {
		if (this.reduceMotion) {
			return;
		}
		const env = this.environment;
		const windPush = (this.state.wind / MAX_WIND) * env.windParticleScale;
		for (const p of this.windParticles) {
			p.x += p.speed * windPush + 0.12 * Math.sign(this.state.wind || 1);
			p.y += Math.sin(this.animTime * 2 + p.x * 0.02) * 0.08;
			if (p.x > WORLD_WIDTH + 10) {
				p.x = -10;
			}
			if (p.x < -10) {
				p.x = WORLD_WIDTH + 10;
			}
		}
	}

	private updateCamera(): void {
		const cp = this.currentPlayer;
		const active = cp?.alive && this.state.phase === 'aiming';
		const sx = this.canvasWidth / WORLD_WIDTH;
		const targetX = active ? (cp!.x * sx - this.canvasWidth * 0.5) * 0.06 : 0;
		const ease = this.reduceMotion ? 1 : 0.05;
		this.cameraX += (targetX - this.cameraX) * ease;
		this.cameraY += (0 - this.cameraY) * ease;
	}

	private render(): void {
		const ctx = this.ctx;
		if (!ctx) {
			return;
		}
		const w = this.canvasWidth;
		const h = this.canvasHeight;
		const sx = w / WORLD_WIDTH;
		const sy = h / WORLD_HEIGHT;

		this.updateCamera();

		ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
		ctx.globalAlpha = 1;
		ctx.globalCompositeOperation = 'source-over';
		ctx.shadowBlur = 0;
		ctx.shadowColor = 'transparent';
		ctx.fillStyle = '#050814';
		ctx.fillRect(0, 0, w, h);
		this.drawSky(ctx, w, h);

		ctx.save();
		ctx.translate(this.cameraX, this.cameraY);

		this.drawTerrain(ctx, sx, sy);
		this.drawNapalm(ctx, sx, sy);
		this.drawExplosions(ctx, sx, sy);
		this.drawDebris(ctx, sx, sy);
		this.drawProjectiles(ctx, sx, sy);
		this.drawTanks(ctx, sx, sy);
		this.drawTrajectoryPreview(ctx, sx, sy);
		this.drawActiveTankHighlight(ctx, sx, sy);

		ctx.restore();

		this.drawScreenAtmosphere(ctx, w, h);
		this.drawWind(ctx, w, h);
	}

	private drawSky(ctx: CanvasRenderingContext2D, w: number, h: number): void {
		const env = this.environment;
		const dayBias = env.cycle;
		const horizonY = h * (0.52 + dayBias * 0.06);
		const grad = ctx.createLinearGradient(0, 0, 0, h);
		if (env.timeOfDay === 'day') {
			grad.addColorStop(0, '#1a3a6e');
			grad.addColorStop(0.35, '#4a7ab8');
			grad.addColorStop(0.72, '#c9a66b');
			grad.addColorStop(1, '#e8c896');
		} else if (env.timeOfDay === 'twilight') {
			grad.addColorStop(0, '#241848');
			grad.addColorStop(0.35, '#5c3d7a');
			grad.addColorStop(0.72, '#8a6a9a');
			grad.addColorStop(1, '#b89888');
		} else {
			grad.addColorStop(0, '#0a1020');
			grad.addColorStop(0.35, '#1a2040');
			grad.addColorStop(0.72, '#2a2848');
			grad.addColorStop(1, '#3a3448');
		}
		ctx.fillStyle = grad;
		ctx.fillRect(0, 0, w, h);

		const horizonGlow = ctx.createLinearGradient(0, horizonY - 8, 0, horizonY + 28);
		horizonGlow.addColorStop(0, 'rgba(255,255,255,0)');
		horizonGlow.addColorStop(0.45, env.skyIsDaylit ? 'rgba(255,230,180,0.22)' : 'rgba(140,160,210,0.14)');
		horizonGlow.addColorStop(1, 'rgba(255,255,255,0)');
		ctx.fillStyle = horizonGlow;
		ctx.fillRect(0, horizonY - 12, w, 40);

		const upperHaze = ctx.createLinearGradient(0, 0, 0, horizonY * 0.65);
		upperHaze.addColorStop(0, 'rgba(255,240,210,0)');
		upperHaze.addColorStop(1, env.skyIsDaylit ? 'rgba(255,235,200,0.08)' : 'rgba(120,130,180,0.06)');
		ctx.fillStyle = upperHaze;
		ctx.fillRect(0, 0, w, horizonY * 0.65);

		this.drawParallaxSilhouettes(ctx, w, h, horizonY, env);

		const haze = ctx.createLinearGradient(0, horizonY * 0.55, 0, h);
		haze.addColorStop(0, 'rgba(255,240,210,0)');
		haze.addColorStop(0.45, colorWithAlpha(env.hazeColor, env.hazeStrength * 0.55));
		haze.addColorStop(
			1,
			env.skyIsDaylit
				? `rgba(255,220,180,${0.14 + env.hazeStrength * 0.32})`
				: `rgba(120,130,180,${0.1 + env.hazeStrength * 0.24})`,
		);
		ctx.fillStyle = haze;
		ctx.fillRect(0, horizonY * 0.35, w, h - horizonY * 0.35);

		const depthHaze = ctx.createLinearGradient(0, horizonY * 0.85, 0, h);
		depthHaze.addColorStop(0, 'rgba(255,255,255,0)');
		depthHaze.addColorStop(1, env.atmosphereTint);
		ctx.fillStyle = depthHaze;
		ctx.fillRect(0, horizonY * 0.55, w, h - horizonY * 0.55);

		const celestialX = w * (0.15 + dayBias * 0.55);
		const celestialY = h * (0.12 + (env.timeOfDay === 'night' ? 0.08 : 0));
		const celestialR = env.timeOfDay === 'day' ? 22 : env.timeOfDay === 'twilight' ? 16 : 10;
		const glow = ctx.createRadialGradient(celestialX, celestialY, 0, celestialX, celestialY, celestialR * 4);
		if (env.timeOfDay === 'day') {
			glow.addColorStop(0, 'rgba(255,248,200,0.95)');
			glow.addColorStop(0.35, 'rgba(255,220,120,0.35)');
		} else if (env.timeOfDay === 'twilight') {
			glow.addColorStop(0, 'rgba(255,180,120,0.75)');
			glow.addColorStop(0.35, 'rgba(255,120,80,0.28)');
		} else {
			glow.addColorStop(0, 'rgba(220,230,255,0.85)');
			glow.addColorStop(0.35, 'rgba(180,200,255,0.25)');
		}
		glow.addColorStop(1, 'rgba(255,255,255,0)');
		ctx.fillStyle = glow;
		ctx.beginPath();
		ctx.arc(celestialX, celestialY, celestialR * 4, 0, Math.PI * 2);
		ctx.fill();
		ctx.fillStyle = env.timeOfDay === 'day' ? '#fff8dc' : env.timeOfDay === 'twilight' ? '#ffd8a8' : '#eef2ff';
		ctx.beginPath();
		ctx.arc(celestialX, celestialY, celestialR, 0, Math.PI * 2);
		ctx.fill();

		const cloudDrift = this.reduceMotion ? 0 : this.animTime * (8 + env.weatherIndex * 2);
		ctx.save();
		ctx.globalAlpha = env.cloudAlpha;
		for (let i = 0; i < 5; i++) {
			const cx = ((i * 173 + cloudDrift + this.state.seed) % (w + 160)) - 80;
			const cy = h * (0.08 + i * 0.055);
			const cw = 60 + (i % 3) * 28;
			ctx.fillStyle = env.skyIsDaylit ? 'rgba(255,255,255,0.72)' : 'rgba(200,205,230,0.45)';
			ctx.beginPath();
			ctx.ellipse(cx, cy, cw, 14 + (i % 2) * 4, 0, 0, Math.PI * 2);
			ctx.ellipse(cx + cw * 0.35, cy - 6, cw * 0.55, 12, 0, 0, Math.PI * 2);
			ctx.ellipse(cx - cw * 0.25, cy + 4, cw * 0.4, 10, 0, 0, Math.PI * 2);
			ctx.fill();
		}
		ctx.restore();

		if (!this.reduceMotion && env.rainDropCount > 0) {
			ctx.save();
			ctx.strokeStyle = env.weather === 'storm' ? 'rgba(190,220,255,0.62)' : 'rgba(205,225,245,0.4)';
			ctx.lineWidth = env.rainLineWidth;
			const windSkew = this.state.wind * 0.08;
			for (let i = 0; i < env.rainDropCount; i++) {
				const rx = (i * 79 + this.animTime * (55 + Math.abs(this.state.wind) * 1.1)) % (w + 80) - 40;
				const ry = (i * 47 + this.animTime * 190) % (h * 0.82);
				ctx.beginPath();
				ctx.moveTo(rx, ry);
				ctx.lineTo(rx + windSkew, ry + (env.weather === 'storm' ? 14 : 9));
				ctx.stroke();
			}
			ctx.restore();
		}

		if (env.weather === 'storm' && !this.reduceMotion) {
			const flash = Math.sin(this.animTime * 17.3) > 0.92 ? 0.18 : 0;
			if (flash > 0) {
				ctx.fillStyle = `rgba(220,235,255,${flash})`;
				ctx.fillRect(0, 0, w, h);
			}
		}

		if (!this.reduceMotion && Math.abs(this.state.wind) > 0 && env.weather !== 'rain' && env.weather !== 'storm') {
			ctx.save();
			for (const p of this.windParticles) {
				ctx.globalAlpha = p.alpha * (0.35 + Math.abs(this.state.wind) / MAX_WIND) * env.windParticleScale;
				ctx.fillStyle = '#f8fafc';
				ctx.fillRect(p.x * (w / WORLD_WIDTH), p.y * (h / WORLD_HEIGHT), p.size * 2, 1);
			}
			ctx.restore();
		}
	}

	private drawParallaxSilhouettes(
		ctx: CanvasRenderingContext2D,
		w: number,
		h: number,
		horizonY: number,
		env: EnvironmentSnapshot,
	): void {
		const seed = this.state.seed + this.state.round * 17;
		const drift = this.reduceMotion ? 0 : this.animTime * (3 + env.weatherIndex * 0.8);
		// Distant ridgelines sit in the sky band just above the near terrain crest so they
		// read as receding hills; farther layers ride higher, hazier and fainter for depth.
		const layers = [
			{ parallax: 0.07, baseFrac: 0.34, amp: 0.11, freq: 0.0035, alpha: env.parallaxAlpha * 0.58, haze: 0.12 },
			{ parallax: 0.13, baseFrac: 0.4, amp: 0.095, freq: 0.0065, alpha: env.parallaxAlpha * 0.78, haze: 0.06 },
			{ parallax: 0.21, baseFrac: 0.47, amp: 0.082, freq: 0.0105, alpha: env.parallaxAlpha * 0.96, haze: 0.02 },
		];

		for (const layer of layers) {
			const offsetX = drift * layer.parallax * 40 - this.cameraX * layer.parallax * 0.35;
			const baseLine = h * layer.baseFrac;
			const ridgeColor = mixTerrainColors(
				env.parallaxColor,
				'#ffffff',
				layer.haze * (0.35 + env.ambientLight * 0.65),
			);

			ctx.save();
			ctx.globalAlpha = layer.alpha;
			ctx.fillStyle = ridgeColor;
			ctx.beginPath();
			ctx.moveTo(-20, h + 10);
			for (let x = -20; x <= w + 20; x += 5) {
				const wx = x + offsetX;
				const ridge =
					Math.sin(wx * layer.freq + seed * 0.01) * h * layer.amp * 0.38 +
					Math.sin(wx * layer.freq * 2.3 + seed * 0.023 + 1.4) * h * layer.amp * 0.2 +
					Math.cos(wx * layer.freq * 0.55 + seed * 0.017) * h * layer.amp * 0.14;
				ctx.lineTo(x, baseLine + ridge);
			}
			ctx.lineTo(w + 20, h + 10);
			ctx.closePath();
			ctx.fill();
			ctx.globalAlpha = layer.alpha * 0.35;
			ctx.strokeStyle = mixTerrainColors(ridgeColor, '#000000', 0.25);
			ctx.lineWidth = 1.5;
			ctx.stroke();
			ctx.restore();
		}
	}

	private drawScreenAtmosphere(ctx: CanvasRenderingContext2D, w: number, h: number): void {
		const env = this.environment;
		const horizonY = h * (0.52 + env.cycle * 0.06);
		const narrowBoost = w < 420 ? 1.32 : 1;

		ctx.save();
		const hazeGrad = ctx.createLinearGradient(0, horizonY * 0.35, 0, h);
		hazeGrad.addColorStop(0, 'rgba(255,255,255,0)');
		hazeGrad.addColorStop(0.45, env.atmosphereTint);
		hazeGrad.addColorStop(1, env.hazeColor);
		ctx.globalAlpha = env.hazeStrength * env.ambientLight * 0.85 * narrowBoost;
		ctx.fillStyle = hazeGrad;
		ctx.fillRect(0, 0, w, h);
		ctx.restore();

		ctx.save();
		const terrainVeil = ctx.createLinearGradient(0, h * 0.42, 0, h);
		terrainVeil.addColorStop(0, 'rgba(255,255,255,0)');
		terrainVeil.addColorStop(0.55, colorWithAlpha(env.hazeColor, env.hazeStrength * 0.22 * narrowBoost));
		terrainVeil.addColorStop(1, colorWithAlpha(env.atmosphereTint, env.hazeStrength * 0.35 * narrowBoost));
		ctx.globalAlpha = env.ambientLight * 0.55 * narrowBoost;
		ctx.fillStyle = terrainVeil;
		ctx.fillRect(0, h * 0.38, w, h * 0.62);
		ctx.restore();

		if (env.weather === 'rain' || env.weather === 'storm') {
			ctx.save();
			ctx.globalAlpha = env.weather === 'storm' ? 0.07 : 0.045;
			ctx.fillStyle = env.weather === 'storm' ? 'rgba(80,100,140,0.32)' : 'rgba(120,140,170,0.24)';
			ctx.fillRect(0, 0, w, horizonY);
			ctx.restore();
		}

		const lightX = w * (0.18 + env.lightAngle * 0.55);
		const lightGrad = ctx.createLinearGradient(lightX - w * 0.2, 0, lightX + w * 0.35, h * 0.55);
		lightGrad.addColorStop(0, 'rgba(255,255,255,0)');
		lightGrad.addColorStop(0.5, env.skyIsDaylit ? 'rgba(255,248,220,0.06)' : 'rgba(180,200,255,0.04)');
		lightGrad.addColorStop(1, 'rgba(255,255,255,0)');
		ctx.save();
		ctx.globalAlpha = env.ambientLight * 0.65;
		ctx.fillStyle = lightGrad;
		ctx.fillRect(0, 0, w, h);
		ctx.restore();
	}

	private drawTerrain(ctx: CanvasRenderingContext2D, sx: number, sy: number): void {
		const env = this.environment;
		const heights = this.state.terrain;
		const worldH = WORLD_HEIGHT * sy;
		const colorSeed = this.state.seed + this.state.round * 31;
		const colorOpts = { seed: colorSeed, worldHeight: WORLD_HEIGHT };

		const traceTerrainPath = (): void => {
			ctx.beginPath();
			ctx.moveTo(0, worldH);
			for (let x = 0; x < heights.length; x++) {
				ctx.lineTo(x * sx, heights[x] * sy);
			}
			ctx.lineTo(WORLD_WIDTH * sx, worldH);
			ctx.closePath();
		};

		ctx.save();
		traceTerrainPath();
		ctx.clip();

		const columnStep = sx < 0.55 ? 3 : 2;
		for (let x = 0; x < heights.length; x += columnStep) {
			const xEnd = Math.min(heights.length - 1, x + columnStep - 1);
			let surfaceSum = 0;
			let scorchSum = 0;
			let slopeMax = 0;
			for (let xi = x; xi <= xEnd; xi++) {
				surfaceSum += heights[xi];
				scorchSum += this.scorchField[xi] ?? 0;
				slopeMax = Math.max(slopeMax, localSlopeAt(heights, xi));
			}
			const span = xEnd - x + 1;
			const surfaceY = (surfaceSum / span) * sy;
			const scorch = scorchSum / span;
			const sampleX = x + (span - 1) * 0.5;
			const sampleIx = Math.max(0, Math.min(heights.length - 1, Math.round(sampleX)));
			const neighborBlend =
				sampleIx > 0 && sampleIx < heights.length - 1
					? (heights[sampleIx - 1] + heights[sampleIx + 1]) * 0.5
					: heights[sampleIx];
			const colGrad = ctx.createLinearGradient(x * sx, surfaceY, x * sx, worldH);
			colGrad.addColorStop(
				0,
				terrainColorAt(heights, this.strata, sampleX, neighborBlend, { ...colorOpts, scorch }),
			);
			for (const layer of this.strata) {
				if (layer.depthStart <= 0) {
					continue;
				}
				const worldY =
					(surfaceSum / span) + layer.depthStart * (WORLD_HEIGHT - surfaceSum / span);
				colGrad.addColorStop(
					layer.depthStart,
					terrainColorAt(heights, this.strata, sampleX, worldY, { ...colorOpts, scorch: scorch * 0.55 }),
				);
			}
			colGrad.addColorStop(
				1,
				terrainColorAt(heights, this.strata, sampleX, WORLD_HEIGHT, { ...colorOpts, scorch: scorch * 0.25 }),
			);
			ctx.fillStyle = colGrad;
			ctx.fillRect(x * sx, surfaceY, columnStep * sx + 0.5, worldH - surfaceY);

			if (slopeMax > 1.2 && x % 2 === 0) {
				const ledgeH = (2 + slopeMax * 0.25) * sy;
				ctx.fillStyle = mixTerrainColors(
					terrainColorAt(heights, this.strata, sampleX, surfaceSum / span + 2, colorOpts),
					'#3a3028',
					Math.min(0.35, slopeMax * 0.06),
				);
				ctx.fillRect(x * sx, surfaceY, columnStep * sx + 0.5, ledgeH);
			}
		}

		for (const layer of this.strata) {
			if (layer.depthStart <= 0.04) {
				continue;
			}
			ctx.save();
			ctx.globalAlpha = 0.24;
			ctx.strokeStyle = mixTerrainColors(layer.top, layer.bottom, 0.45);
			ctx.lineWidth = 1.25;
			ctx.beginPath();
			let started = false;
			for (let x = 0; x < heights.length; x++) {
				const seamY = (heights[x] + layer.depthStart * (WORLD_HEIGHT - heights[x])) * sy;
				if (x === 0) {
					ctx.moveTo(0, seamY);
					started = true;
				} else {
					ctx.lineTo(x * sx, seamY);
				}
			}
			if (started) {
				ctx.stroke();
			}
			ctx.restore();
		}

		ctx.save();
		ctx.globalAlpha = 0.55;
		ctx.strokeStyle = mixTerrainColors(this.strata[0].top, '#c8e8a0', 0.35);
		ctx.lineWidth = 1.5;
		ctx.beginPath();
		ctx.moveTo(0, heights[0] * sy);
		for (let x = 1; x < heights.length; x++) {
			ctx.lineTo(x * sx, heights[x] * sy);
		}
		ctx.stroke();
		ctx.restore();

		ctx.restore();

		ctx.save();
		traceTerrainPath();
		ctx.clip();

		for (let x = 1; x < heights.length - 1; x++) {
			const rim = craterRimStrength(heights, x);
			if (rim < 0.12) {
				continue;
			}
			const surface = heights[x] * sy;
			const scorch = this.scorchField[x] ?? 0;
			const wallDepth = (6 + rim * 14) * sy;
			const bandH = Math.max(1.5, 2.2 * sy);
			for (let dy = 0; dy < wallDepth; dy += bandH) {
				const worldY = heights[x] + dy / sy;
				ctx.fillStyle = terrainColorAt(heights, this.strata, x, worldY, {
					...colorOpts,
					scorch: scorch * (1 - dy / wallDepth) * 0.5,
				});
				ctx.fillRect(x * sx, surface + dy, sx + 0.5, bandH + 0.5);
			}
			ctx.strokeStyle = `rgba(255,228,196,${rim * 0.48})`;
			ctx.lineWidth = 1;
			ctx.beginPath();
			ctx.moveTo((x - 1) * sx, heights[x - 1] * sy);
			ctx.lineTo(x * sx, surface);
			ctx.lineTo((x + 1) * sx, heights[x + 1] * sy);
			ctx.stroke();
		}

		ctx.restore();

		ctx.save();
		traceTerrainPath();
		ctx.clip();
		const shadeStrength = env.terrainShadeAlpha + (1 - env.ambientLight) * 0.08;
		const surfaceShade = ctx.createLinearGradient(0, 0, 0, worldH * 0.22);
		surfaceShade.addColorStop(0, colorWithAlpha(env.terrainShadow, shadeStrength));
		surfaceShade.addColorStop(1, 'rgba(0,0,0,0)');
		ctx.fillStyle = surfaceShade;
		ctx.fillRect(0, 0, WORLD_WIDTH * sx, worldH);
		ctx.restore();

		ctx.save();
		traceTerrainPath();
		ctx.clip();
		const sunX = env.lightAngle * WORLD_WIDTH * sx;
		const dirLight = ctx.createLinearGradient(
			sunX - WORLD_WIDTH * sx * 0.35,
			0,
			sunX + WORLD_WIDTH * sx * 0.45,
			0,
		);
		dirLight.addColorStop(0, colorWithAlpha(env.terrainShadow, env.terrainShadeAlpha * 0.55));
		dirLight.addColorStop(0.42, 'rgba(0,0,0,0)');
		dirLight.addColorStop(0.58, 'rgba(0,0,0,0)');
		dirLight.addColorStop(1, colorWithAlpha(env.terrainHighlight, env.ambientLight * 0.14));
		ctx.globalAlpha = env.skyIsDaylit ? 0.85 : 0.72;
		ctx.fillStyle = dirLight;
		ctx.fillRect(0, 0, WORLD_WIDTH * sx, worldH);
		ctx.restore();

		ctx.save();
		ctx.lineJoin = 'round';
		ctx.lineCap = 'round';
		ctx.shadowColor = env.terrainShadow;
		ctx.shadowBlur = 3;
		ctx.shadowOffsetY = 1;
		ctx.strokeStyle = env.terrainShadow;
		ctx.lineWidth = 3;
		ctx.beginPath();
		ctx.moveTo(0, heights[0] * sy);
		for (let x = 1; x < heights.length; x++) {
			ctx.lineTo(x * sx, heights[x] * sy);
		}
		ctx.stroke();
		ctx.shadowBlur = 0;
		ctx.shadowOffsetY = 0;
		ctx.strokeStyle = env.terrainHighlight;
		ctx.lineWidth = 1.25;
		ctx.stroke();
		ctx.restore();

		ctx.save();
		const lightSide = env.lightAngle >= 0.5 ? 1 : -1;
		for (let x = 0; x < heights.length; x += 4) {
			const slope = localSlopeAt(heights, x);
			if (slope < 0.8) {
				continue;
			}
			const slopeDir = x > 0 && x < heights.length - 1 ? heights[x + 1] - heights[x - 1] : 0;
			const sunFacing = slopeDir * lightSide > 0;
			const surface = heights[x] * sy;
			const scorch = this.scorchField[x] ?? 0;
			const edgeH = (1.5 + slope * 0.35) * sy;
			const edgeGrad = ctx.createLinearGradient(x * sx, surface - edgeH, x * sx, surface + edgeH * 0.6);
			edgeGrad.addColorStop(
				0,
				mixTerrainColors(
					terrainColorAt(heights, this.strata, x, heights[x] - 1, { ...colorOpts, scorch }),
					env.skyIsDaylit ? '#c8e8a0' : '#a8b8d8',
					sunFacing ? 0.4 : 0.18,
				),
			);
			edgeGrad.addColorStop(1, 'rgba(0,0,0,0)');
			ctx.fillStyle = edgeGrad;
			ctx.fillRect(x * sx, surface - edgeH, sx * 4 + 0.5, edgeH * 1.6);
		}
		ctx.restore();
	}

	private blendColor(base: string, burn: string, t: number): string {
		return mixTerrainColors(base, burn, t);
	}

	private drawTanks(ctx: CanvasRenderingContext2D, sx: number, sy: number): void {
		const activeId = this.state.phase === 'aiming' ? this.currentPlayer?.id : null;
		for (const tank of this.state.players) {
			if (!tank.alive) {
				continue;
			}
			this.drawTank(ctx, tank, sx, sy, tank.id === activeId);
		}
	}

	private drawTank(
		ctx: CanvasRenderingContext2D,
		tank: Tank,
		sx: number,
		sy: number,
		isActive: boolean,
	): void {
		const env = this.environment;
		const x = tank.x * sx;
		const y = tank.y * sy;
		const tw = TANK_WIDTH * sx;
		const th = TANK_HEIGHT * sy;
		const facingRight = tank.x < WORLD_WIDTH / 2;
		const recoil = this.tankRecoil.get(tank.id) ?? 0;
		const hp = tank.health / tank.maxHealth;
		const damageTier = hp > 0.65 ? 0 : hp > 0.35 ? 1 : 2;
		const heights = this.state.terrain;
		const slopeMag = localSlopeAt(heights, tank.x);
		const ix = Math.max(1, Math.min(heights.length - 2, Math.floor(tank.x)));
		const slopeDelta = heights[ix + 1] - heights[ix - 1];
		const shadowSkew = Math.max(-0.5, Math.min(0.5, slopeDelta * 0.045));
		const shadowRx = tw * (0.66 + (1 - env.ambientLight) * 0.12 + slopeMag * 0.02);
		const shadowRy = Math.max(4.2, 6.2 * sy) * (env.weather === 'storm' ? 1.1 : 1);
		const shadowAlpha = env.tankShadowAlpha * (1.08 + (1 - env.ambientLight) * 0.34);
		const shadowFootY = y + (1.2 + slopeMag * 0.14) * sy;

		ctx.save();
		ctx.translate(x, shadowFootY);
		ctx.transform(1, 0, shadowSkew, 1, 0, 0);
		const shadowGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, shadowRx);
		shadowGrad.addColorStop(0, `rgba(0,0,0,${shadowAlpha})`);
		shadowGrad.addColorStop(0.45, `rgba(0,0,0,${shadowAlpha * 0.62})`);
		shadowGrad.addColorStop(1, 'rgba(0,0,0,0)');
		ctx.fillStyle = shadowGrad;
		ctx.beginPath();
		ctx.ellipse(0, 0, shadowRx, shadowRy, 0, 0, Math.PI * 2);
		ctx.fill();
		ctx.restore();

		ctx.save();
		ctx.translate(x, y - th);
		ctx.scale(facingRight ? 1 : -1, 1);

		const trackY = th * 0.78;
		const trackH = th * 0.24;
		ctx.fillStyle = '#1f2937';
		ctx.beginPath();
		ctx.roundRect(-tw * 0.5, trackY, tw, trackH, 2);
		ctx.fill();
		ctx.strokeStyle = 'rgba(0,0,0,0.45)';
		ctx.lineWidth = 1;
		ctx.stroke();

		const wheelCount = 5;
		const wheelSpan = tw * 0.72;
		const wheelStart = -wheelSpan / 2;
		for (let i = 0; i < wheelCount; i++) {
			const wheelX = wheelStart + (i / (wheelCount - 1)) * wheelSpan;
			ctx.fillStyle = i % 2 === 0 ? '#0f172a' : '#334155';
			ctx.beginPath();
			ctx.arc(wheelX, trackY + trackH * 0.55, th * 0.11, 0, Math.PI * 2);
			ctx.fill();
			ctx.strokeStyle = 'rgba(148,163,184,0.35)';
			ctx.lineWidth = 0.8;
			ctx.stroke();
		}

		const hullGrad = ctx.createLinearGradient(0, th * 0.12, 0, th * 0.72);
		hullGrad.addColorStop(0, this.blendColor(tank.color, '#ffffff', 0.32));
		hullGrad.addColorStop(0.5, tank.color);
		hullGrad.addColorStop(1, this.blendColor(tank.color, '#000000', 0.42));

		ctx.fillStyle = this.blendColor(tank.color, '#000000', 0.4);
		ctx.beginPath();
		ctx.moveTo(-tw * 0.46, th * 0.72);
		ctx.lineTo(-tw * 0.4, th * 0.28);
		ctx.lineTo(tw * 0.24, th * 0.16);
		ctx.lineTo(tw * 0.52, th * 0.42);
		ctx.lineTo(tw * 0.5, th * 0.72);
		ctx.closePath();
		ctx.fill();

		ctx.fillStyle = hullGrad;
		ctx.beginPath();
		ctx.moveTo(-tw * 0.42, th * 0.68);
		ctx.lineTo(-tw * 0.36, th * 0.32);
		ctx.lineTo(tw * 0.22, th * 0.2);
		ctx.lineTo(tw * 0.47, th * 0.44);
		ctx.lineTo(tw * 0.45, th * 0.68);
		ctx.closePath();
		ctx.fill();

		ctx.strokeStyle = this.blendColor(tank.color, '#ffffff', 0.55 + env.tankLightTint);
		ctx.lineWidth = Math.max(0.8, 1 * sx);
		ctx.beginPath();
		ctx.moveTo(-tw * 0.32, th * 0.3);
		ctx.lineTo(tw * 0.2, th * 0.22);
		ctx.stroke();

		const turretGrad = ctx.createLinearGradient(0, -th * 0.04, 0, th * 0.32);
		turretGrad.addColorStop(0, this.blendColor(tank.color, '#ffffff', 0.42));
		turretGrad.addColorStop(1, this.blendColor(tank.color, '#000000', 0.32));
		ctx.fillStyle = turretGrad;
		ctx.beginPath();
		ctx.ellipse(tw * 0.02, th * 0.28, tw * 0.26, th * 0.32, 0, Math.PI, 0);
		ctx.closePath();
		ctx.fill();

		ctx.fillStyle = this.blendColor(tank.color, '#e5f2ff', 0.55 + env.tankLightTint);
		ctx.beginPath();
		ctx.ellipse(-tw * 0.04, th * 0.12, tw * 0.09, th * 0.11, 0, 0, Math.PI * 2);
		ctx.fill();

		const rad = (tank.angle * Math.PI) / 180;
		const barrelLen = (20 - recoil * 6) * sx;
		const pivotY = -th * 0.02;
		const bx = Math.cos(rad) * barrelLen;
		const by = pivotY - Math.sin(rad) * barrelLen;
		ctx.strokeStyle = '#374151';
		ctx.lineWidth = 5 * sx;
		ctx.lineCap = 'round';
		ctx.beginPath();
		ctx.moveTo(0, pivotY);
		ctx.lineTo(bx, by);
		ctx.stroke();
		ctx.strokeStyle = this.blendColor(tank.color, '#94a3b8', 0.25);
		ctx.lineWidth = 2.5 * sx;
		ctx.stroke();

		if (damageTier > 0) {
			ctx.strokeStyle = `rgba(15,23,42,${damageTier === 1 ? 0.35 : 0.55})`;
			ctx.lineWidth = 1.2;
			ctx.beginPath();
			ctx.moveTo(-tw * 0.15, th * 0.3);
			ctx.lineTo(-tw * 0.05, th * 0.45);
			ctx.moveTo(tw * 0.1, th * 0.28);
			ctx.lineTo(tw * 0.18, th * 0.38);
			ctx.stroke();
		}
		if (damageTier === 2 && !this.reduceMotion) {
			ctx.fillStyle = `rgba(100,116,139,${0.25 + Math.sin(this.animTime * 5) * 0.08})`;
			ctx.beginPath();
			ctx.arc(tw * 0.05, th * 0.18, tw * 0.08, 0, Math.PI * 2);
			ctx.fill();
		}

		ctx.restore();

		const labelY = y - (TANK_HEIGHT + 18) * sy;
		ctx.save();
		ctx.textAlign = 'center';
		ctx.font = `600 ${Math.max(10, 11 * sx)}px system-ui, sans-serif`;
		ctx.fillStyle = 'rgba(8, 15, 24, 0.88)';
		ctx.fillText(tank.name, x, labelY);
		ctx.font = `700 ${Math.max(9, 10 * sx)}px system-ui, sans-serif`;
		ctx.fillStyle = tank.kind === 'cpu' ? '#fca5a5' : '#86efac';
		ctx.fillText(tank.kind === 'cpu' ? 'CPU' : 'PILOT', x, labelY + 11 * sy);
		if (isActive) {
			ctx.fillStyle = '#fef08a';
			ctx.fillText('ACTIVE', x, labelY - 11 * sy);
		}
		ctx.restore();

		const barW = 36 * sx;
		ctx.fillStyle = 'rgba(0,0,0,0.5)';
		ctx.fillRect(x - barW / 2, y - (TANK_HEIGHT + 8) * sy, barW, 5 * sy);
		ctx.fillStyle = hp > 0.35 ? '#6ee7a0' : '#f87171';
		ctx.fillRect(x - barW / 2, y - (TANK_HEIGHT + 8) * sy, barW * hp, 5 * sy);
	}

	private drawActiveTankHighlight(ctx: CanvasRenderingContext2D, sx: number, sy: number): void {
		const cp = this.currentPlayer;
		if (!cp?.alive || this.state.phase !== 'aiming') {
			return;
		}
		const pulse = this.reduceMotion ? 1 : 0.82 + Math.sin(this.animTime * 4) * 0.18;
		const x = cp.x * sx;
		const y = (cp.y - TANK_HEIGHT) * sy;
		const ringColor = cp.kind === 'cpu' ? '255,120,120' : '255,255,255';
		ctx.save();
		ctx.strokeStyle = `rgba(${ringColor},${0.42 * pulse})`;
		ctx.lineWidth = 2.5;
		ctx.setLineDash(cp.kind === 'cpu' ? [6, 4] : []);
		ctx.beginPath();
		ctx.ellipse(x, y, TANK_WIDTH * sx * 0.9 * pulse, TANK_HEIGHT * sy * 1.75, 0, 0, Math.PI * 2);
		ctx.stroke();
		ctx.setLineDash([]);
		ctx.restore();
	}

	private drawProjectiles(ctx: CanvasRenderingContext2D, sx: number, sy: number): void {
		for (const p of this.state.projectiles) {
			const px = p.x * sx;
			const py = p.y * sy;
			const visual = WEAPON_VISUALS[p.weaponId];
			const speedAngle = Math.atan2(-p.vy, p.vx);

			if (!this.reduceMotion && p.trail.length > 1) {
				const trailGrad = ctx.createLinearGradient(
					p.trail[0].x * sx,
					p.trail[0].y * sy,
					px,
					py,
				);
				trailGrad.addColorStop(0, visual.trailStart);
				trailGrad.addColorStop(1, visual.trailEnd);
				ctx.strokeStyle = trailGrad;
				ctx.lineWidth = visual.trailWidth;
				ctx.beginPath();
				p.trail.forEach((pt, i) => {
					if (i === 0) {
						ctx.moveTo(pt.x * sx, pt.y * sy);
					} else {
						ctx.lineTo(pt.x * sx, pt.y * sy);
					}
				});
				ctx.stroke();
			}

			const glowR = visual.projectileRadius * 2.2;
			const glow = ctx.createRadialGradient(px, py, 0, px, py, glowR);
			glow.addColorStop(0, visual.glowColor);
			glow.addColorStop(1, 'rgba(255,255,255,0)');
			ctx.fillStyle = glow;
			ctx.beginPath();
			ctx.arc(px, py, glowR, 0, Math.PI * 2);
			ctx.fill();

			ctx.save();
			ctx.translate(px, py);
			ctx.rotate(speedAngle);
			ctx.fillStyle = visual.coreColor;
			if (visual.shape === 'dart') {
				ctx.beginPath();
				ctx.moveTo(visual.projectileRadius * 1.6, 0);
				ctx.lineTo(-visual.projectileRadius * 1.2, visual.projectileRadius * 0.7);
				ctx.lineTo(-visual.projectileRadius * 0.8, 0);
				ctx.lineTo(-visual.projectileRadius * 1.2, -visual.projectileRadius * 0.7);
				ctx.closePath();
				ctx.fill();
			} else if (visual.shape === 'capsule') {
				ctx.beginPath();
				ctx.roundRect(-visual.projectileRadius * 1.1, -visual.projectileRadius * 0.55, visual.projectileRadius * 2.2, visual.projectileRadius * 1.1, visual.projectileRadius);
				ctx.fill();
			} else if (visual.shape === 'cluster') {
				ctx.beginPath();
				ctx.arc(0, 0, visual.projectileRadius, 0, Math.PI * 2);
				ctx.fill();
				ctx.fillStyle = '#a5f3fc';
				ctx.beginPath();
				ctx.arc(visual.projectileRadius * 0.55, 0, visual.projectileRadius * 0.45, 0, Math.PI * 2);
				ctx.arc(-visual.projectileRadius * 0.45, visual.projectileRadius * 0.35, visual.projectileRadius * 0.35, 0, Math.PI * 2);
				ctx.fill();
			} else if (visual.shape === 'flame') {
				ctx.beginPath();
				ctx.moveTo(visual.projectileRadius * 1.2, 0);
				ctx.quadraticCurveTo(0, -visual.projectileRadius, -visual.projectileRadius * 1.1, 0);
				ctx.quadraticCurveTo(0, visual.projectileRadius, visual.projectileRadius * 1.2, 0);
				ctx.fill();
			} else {
				ctx.beginPath();
				ctx.arc(0, 0, visual.projectileRadius, 0, Math.PI * 2);
				ctx.fill();
			}
			ctx.restore();
		}
	}

	private drawExplosions(ctx: CanvasRenderingContext2D, sx: number, sy: number): void {
		for (const ex of this.state.explosions) {
			const t = ex.age / ex.maxAge;
			const cx = ex.x * sx;
			const cy = ex.y * sy;
			const visual = WEAPON_VISUALS[ex.weaponId];
			const r = ex.radius * (0.35 + t * 0.95) * sx;
			const alpha = 1 - t;

			if (!this.reduceMotion && visual.shockwave && t < 0.7) {
				const shockR = ex.radius * (0.5 + t * 1.4) * sx;
				ctx.strokeStyle = `rgba(255,255,255,${(1 - t) * 0.35})`;
				ctx.lineWidth = visual.impactStyle === 'mushroom' ? 3 : 2;
				ctx.beginPath();
				ctx.arc(cx, cy, shockR, 0, Math.PI * 2);
				ctx.stroke();
			}

			const fireGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
			if (visual.impactStyle === 'napalm') {
				fireGrad.addColorStop(0, `rgba(255,220,80,${alpha})`);
				fireGrad.addColorStop(0.5, `rgba(255,100,20,${alpha * 0.7})`);
				fireGrad.addColorStop(1, 'rgba(80,20,0,0)');
			} else if (visual.impactStyle === 'mushroom') {
				fireGrad.addColorStop(0, `rgba(255,255,240,${alpha})`);
				fireGrad.addColorStop(0.25, `rgba(255,160,60,${alpha * 0.85})`);
				fireGrad.addColorStop(0.6, `rgba(200,60,20,${alpha * 0.5})`);
				fireGrad.addColorStop(1, 'rgba(60,20,10,0)');
			} else if (visual.impactStyle === 'cluster') {
				fireGrad.addColorStop(0, `rgba(200,250,255,${alpha})`);
				fireGrad.addColorStop(0.35, `rgba(34,211,238,${alpha * 0.75})`);
				fireGrad.addColorStop(1, 'rgba(10,40,60,0)');
			} else if (visual.impactStyle === 'spark') {
				fireGrad.addColorStop(0, `rgba(255,255,255,${alpha})`);
				fireGrad.addColorStop(0.4, `rgba(255,220,120,${alpha * 0.65})`);
				fireGrad.addColorStop(1, 'rgba(80,40,10,0)');
			} else {
				fireGrad.addColorStop(0, `rgba(255,240,160,${alpha})`);
				fireGrad.addColorStop(0.45, `rgba(255,140,40,${alpha * 0.75})`);
				fireGrad.addColorStop(1, 'rgba(80,30,10,0)');
			}
			ctx.fillStyle = fireGrad;
			ctx.beginPath();
			ctx.arc(cx, cy, r, 0, Math.PI * 2);
			ctx.fill();

			if (visual.impactStyle === 'mushroom' && t > 0.15 && t < 0.85) {
				const stemW = r * 0.22;
				const stemH = r * 0.9;
				ctx.fillStyle = `rgba(255,180,80,${alpha * 0.45})`;
				ctx.fillRect(cx - stemW / 2, cy - stemH, stemW, stemH);
				ctx.fillStyle = `rgba(240,240,230,${alpha * 0.35})`;
				ctx.beginPath();
				ctx.ellipse(cx, cy - stemH, r * 0.55, r * 0.28, 0, 0, Math.PI * 2);
				ctx.fill();
			}

			if (!this.reduceMotion && visual.smoke && t > 0.2 && t < 0.85) {
				const smokeR = r * (0.8 + (t - 0.2) * 0.6);
				ctx.fillStyle = `rgba(60,55,50,${alpha * 0.35})`;
				ctx.beginPath();
				ctx.arc(cx, cy - smokeR * 0.3, smokeR, 0, Math.PI * 2);
				ctx.fill();
			}
		}
	}

	private drawDebris(ctx: CanvasRenderingContext2D, sx: number, sy: number): void {
		for (const d of this.debrisFx) {
			const lifeT = 1 - d.life / d.maxLife;
			ctx.fillStyle = `hsla(${d.hue}, 70%, 45%, ${lifeT * 0.85})`;
			ctx.fillRect(d.x * sx - d.size, d.y * sy - d.size, d.size * 2, d.size * 2);
		}
	}

	private drawNapalm(ctx: CanvasRenderingContext2D, sx: number, sy: number): void {
		for (const zone of this.state.napalmZones) {
			const pulse = this.reduceMotion ? 1 : 0.88 + Math.sin(zone.remainingTicks * 0.12 + this.animTime * 6) * 0.12;
			const zx = zone.x * sx;
			const zy = zone.y * sy;
			const rx = zone.radius * sx * pulse;
			const ry = zone.radius * sy * 0.38 * pulse;

			const poolGrad = ctx.createRadialGradient(zx, zy, 0, zx, zy, rx);
			poolGrad.addColorStop(0, 'rgba(255,220,80,0.55)');
			poolGrad.addColorStop(0.45, 'rgba(255,90,10,0.42)');
			poolGrad.addColorStop(1, 'rgba(120,20,0,0.08)');
			ctx.beginPath();
			ctx.ellipse(zx, zy, rx, ry, 0, 0, Math.PI * 2);
			ctx.fillStyle = poolGrad;
			ctx.fill();

			if (!this.reduceMotion) {
				for (let i = 0; i < 4; i++) {
					const fx = zx + Math.sin(zone.remainingTicks * 0.05 + i * 1.7) * rx * 0.35;
					const fy = zy - Math.abs(Math.cos(zone.remainingTicks * 0.04 + i)) * ry * 0.5;
					ctx.fillStyle = `rgba(255,180,40,${0.25 + (i % 2) * 0.1})`;
					ctx.beginPath();
					ctx.ellipse(fx, fy, rx * 0.18, ry * 0.35, 0, 0, Math.PI * 2);
					ctx.fill();
				}
			}

			ctx.strokeStyle = 'rgba(255,120,30,0.35)';
			ctx.lineWidth = 1;
			ctx.beginPath();
			ctx.ellipse(zx, zy, rx, ry, 0, 0, Math.PI * 2);
			ctx.stroke();
		}
	}

	private drawTrajectoryPreview(ctx: CanvasRenderingContext2D, sx: number, sy: number): void {
		const p = this.currentPlayer;
		if (!p || this.state.phase !== 'aiming') {
			return;
		}
		const facingRight = p.x < WORLD_WIDTH / 2;
		const points = buildTrajectoryPreview({
			angle: p.angle,
			power: p.power,
			startX: p.x,
			startY: p.y - TANK_HEIGHT,
			facingRight,
			weaponId: this.state.selectedWeapon,
			wind: this.state.wind,
			worldWidth: WORLD_WIDTH,
			worldHeight: WORLD_HEIGHT,
			terrain: this.state.terrain,
			seed: this.state.seed + p.angle * 3 + p.power,
		});
		if (points.length < 2) {
			return;
		}

		ctx.save();
		ctx.setLineDash([4, 6]);
		ctx.strokeStyle = 'rgba(255,248,200,0.45)';
		ctx.lineWidth = 1.5;
		ctx.beginPath();
		points.forEach((pt, i) => {
			if (i === 0) {
				ctx.moveTo(pt.x * sx, pt.y * sy);
			} else {
				ctx.lineTo(pt.x * sx, pt.y * sy);
			}
		});
		ctx.stroke();
		ctx.setLineDash([]);

		points.forEach((pt, i) => {
			if (i === 0 || i % 2 !== 0) {
				return;
			}
			ctx.beginPath();
			ctx.arc(pt.x * sx, pt.y * sy, 2.5, 0, Math.PI * 2);
			ctx.fillStyle = 'rgba(255,220,120,0.55)';
			ctx.fill();
		});

		const windHint = (this.state.wind / MAX_WIND) * 18 * sx;
		if (Math.abs(windHint) > 1) {
			ctx.strokeStyle = 'rgba(180,210,255,0.35)';
			ctx.lineWidth = 1;
			ctx.beginPath();
			ctx.moveTo(points[0].x * sx, points[0].y * sy - 8);
			ctx.quadraticCurveTo(
				points[Math.floor(points.length / 2)].x * sx + windHint,
				points[Math.floor(points.length / 2)].y * sy,
				points[points.length - 1].x * sx + windHint * 0.5,
				points[points.length - 1].y * sy,
			);
			ctx.stroke();
		}
		ctx.restore();
	}

	private drawWind(ctx: CanvasRenderingContext2D, w: number, h: number): void {
		const pad = 12;
		const barW = Math.min(120, w * 0.28);
		const cx = w - pad - barW / 2;
		const cy = pad + 6;
		const len = (this.state.wind / MAX_WIND) * (barW * 0.42);

		ctx.fillStyle = 'rgba(0,0,0,0.35)';
		ctx.beginPath();
		ctx.roundRect(cx - barW / 2 - 6, cy - 10, barW + 12, 22, 6);
		ctx.fill();

		ctx.strokeStyle = 'rgba(255,255,255,0.25)';
		ctx.lineWidth = 1;
		ctx.beginPath();
		ctx.moveTo(cx - barW / 2, cy);
		ctx.lineTo(cx + barW / 2, cy);
		ctx.stroke();

		ctx.strokeStyle = this.state.wind === 0 ? '#a8b4c8' : this.state.wind > 0 ? '#7dd3fc' : '#fca5a5';
		ctx.lineWidth = 2.5;
		ctx.lineCap = 'round';
		ctx.beginPath();
		ctx.moveTo(cx, cy);
		ctx.lineTo(cx + len, cy);
		ctx.stroke();

		if (len !== 0) {
			const tipX = cx + len;
			const dir = len > 0 ? 1 : -1;
			ctx.beginPath();
			ctx.moveTo(tipX, cy);
			ctx.lineTo(tipX - dir * 6, cy - 4);
			ctx.lineTo(tipX - dir * 6, cy + 4);
			ctx.closePath();
			ctx.fillStyle = ctx.strokeStyle as string;
			ctx.fill();
		}

		ctx.fillStyle = '#f1f5f9';
		ctx.font = '600 10px system-ui, sans-serif';
		ctx.textAlign = 'center';
		ctx.fillText(`Wind ${this.windLabel}`, cx, cy - 12);
	}

	ammoFor(playerId: number, weaponId: WeaponId): number {
		return this.state.players.find((p) => p.id === playerId)?.inventory[weaponId] ?? 0;
	}

	private bindFullscreenListener(): void {
		if (typeof document === 'undefined') {
			return;
		}
		const handler = () => {
			this.syncFullscreenFlag();
			this.resizeCanvas();
			this.cdr.detectChanges();
		};
		document.addEventListener('fullscreenchange', handler);
		this.removeFullscreenListener = () => document.removeEventListener('fullscreenchange', handler);
	}

	private unbindFullscreenListener(): void {
		this.removeFullscreenListener?.();
		this.removeFullscreenListener = null;
	}

	private syncFullscreenFlag(): void {
		this.isFullscreen = typeof document !== 'undefined' && !!document.fullscreenElement;
	}

	private hydratePersistedProgress(): void {
		if (typeof localStorage === 'undefined') {
			return;
		}
		try {
			const raw = localStorage.getItem(PERSIST_KEY);
			if (!raw) {
				return;
			}
			const saved = JSON.parse(raw) as PersistedProgress;
			if (!saved || saved.version !== 1 || !Array.isArray(saved.players) || !saved.players.length) {
				return;
			}
			const base = createInitialState(saved.seed ?? 1337, saved.players.length);
			this.playerCount = saved.players.length;
			this.state = {
				...base,
				bestOf: saved.bestOf || base.bestOf,
				round: saved.round || 1,
				selectedWeapon: saved.selectedWeapon || base.selectedWeapon,
				players: base.players.map((p, i) => {
					const s = saved.players[i];
					if (!s) {
						return p;
					}
					return {
						...p,
						name: s.name || p.name,
						kind: s.kind || p.kind,
						difficulty: s.difficulty ?? p.difficulty,
						roundWins: s.roundWins ?? 0,
						credits: s.credits ?? p.credits,
						color: s.color || p.color,
						inventory: cloneInventory(s.inventory || p.inventory),
					};
				}),
				message: 'Saved inventory restored — configure and start when ready.',
			};
		} catch {
			this.clearPersistedProgress();
		}
	}

	private persistProgress(): void {
		if (typeof localStorage === 'undefined') {
			return;
		}
		if (this.state.phase === 'setup' || this.state.phase === 'gameOver') {
			return;
		}
		const payload: PersistedProgress = {
			version: 1,
			seed: this.state.seed,
			bestOf: this.state.bestOf,
			round: this.state.round,
			selectedWeapon: this.state.selectedWeapon,
			players: this.state.players.map((p) => ({
				id: p.id,
				name: p.name,
				kind: p.kind,
				difficulty: p.difficulty,
				roundWins: p.roundWins,
				credits: p.credits,
				inventory: cloneInventory(p.inventory),
				color: p.color,
			})),
		};
		try {
			localStorage.setItem(PERSIST_KEY, JSON.stringify(payload));
		} catch {
			// Ignore quota / private-mode failures.
		}
	}

	private clearPersistedProgress(): void {
		if (typeof localStorage === 'undefined') {
			return;
		}
		try {
			localStorage.removeItem(PERSIST_KEY);
		} catch {
			// ignore
		}
	}

	private recordMatchBests(): void {
		const storage = typeof localStorage !== 'undefined' ? localStorage : null;
		this.bestScores = saveBestScoresToStorage(
			storage,
			mergeBestScores(this.bestScores, buildMatchStandings(this.state.players)),
		);
	}
}
